import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuthStore } from "../service/authStore";
import {
  Clock3,
  MapPin,
  Download,
  ChevronLeft,
  FileText,
  Pencil,
  ArrowLeft,
} from "lucide-react";
import { FaFilePdf } from "react-icons/fa6";
import ConfirmModal from "./ConfirmModal";
import "./MomPage.css";
import { generateMomPdf } from "../pdf/MomPdf";
import pdfIcon from "../assets/pdficon.png";

const MOM_API_URL = process.env.REACT_APP_API_URL;

const statusTabs = [
  "All",
  "Upcoming",
  "Completed",
  "Draft",
  "Cancelled",
  "Overdue",
];

// Keys normalized to UPPERCASE — lookup will also uppercase meeting.status
const statusStyleMap = {
  UPCOMING: "badge-upcoming",
  COMPLETED: "badge-completed",
  DRAFT: "badge-draft",
  CANCELLED: "badge-cancelled",
  OVERDUE: "badge-overdue",
};

// Keys normalized to lowercase — lookup will also lowercase point.status
const pointStatusStyleMap = {
  "in progress": "point-status-inprogress",
  open: "point-status-open",
  closed: "point-status-closed",
};

function getStatusBadgeClass(status) {
  if (!status) return "";
  return statusStyleMap[status.toUpperCase()] || "";
}

function getPointStatusClass(status) {
  if (!status) return "";
  return pointStatusStyleMap[status.toLowerCase()] || "";
}

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatFullDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  const [hh, mm] = timeStr.split(":");
  const d = new Date();
  d.setHours(hh, mm);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// Display-only helper: shows a full name in short form, e.g.
// "Abhishek Pachpol" -> "Abhishek P". The full name is still what's stored
// and sent to the backend everywhere else — this only touches what's shown
// on screen (chips, badges, table cells).
function shortenName(fullName) {
  if (!fullName) return "";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return parts[0] || "";
  return `${parts[0]} ${parts[1].charAt(0).toUpperCase()}`;
}

// ============================================================================
// NEW: Helper function to check if date is today
// ============================================================================
function isTodayDate(dateStr) {
  const today = new Date();
  const date = new Date(dateStr);
  return date.toDateString() === today.toDateString();
}

// ============================================================================
// CHANGED: Group by meeting_date instead of created_at
// ============================================================================
function groupByMeetingDate(meetings) {
  const groups = {};
  meetings.forEach((m) => {
    const dateKey = m.meeting_date; // Changed from m.created_at
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(m);
  });
  // Sort descending: future dates first (top), past dates below
  return Object.keys(groups)
    .sort((a, b) => new Date(b) - new Date(a)) // Descending: newest first
    .map((date) => ({ date, items: groups[date] }));
}

// Builds a MomUpdate-shaped payload (PUT /mom/{company_id}/{mom_id}) from an
// already-loaded meeting detail, forcing a specific status. Used to publish
// straight from the detail panel without going through the edit form.
//
// Status is kept UPPERCASE ("COMPLETED") to match the convention used
// everywhere else — statusStyleMap's keys, the tab->query param conversion
// below, and what AddMom/EditMom send on Publish. Mixing case here would
// make those `=== "COMPLETED"` checks silently fail.
function buildUpdatePayloadFromDetail(detail, status) {
  return {
    meeting_title: detail.meeting_title,
    meeting_type: detail.meeting_type,
    purpose: detail.purpose,
    meeting_date: detail.meeting_date,
    start_time: detail.start_time,
    end_time: detail.end_time,
    actual_m_start_time: detail.actual_m_start_time,
    actual_m_end_time: detail.actual_m_end_time,
    location: detail.location,
    meeting_mode: detail.meeting_mode,
    meeting_link: detail.meeting_link,
    organized_by_user_id: detail.organized_by_user_id,
    organized_by_name: detail.organized_by_name,
    organized_by_email: detail.organized_by_email,
    prepared_by_user_id: detail.prepared_by_user_id,
    prepared_by_name: detail.prepared_by_name,
    prepared_by_email: detail.prepared_by_email,
    status,
    attendance: (detail.attendance || []).map((a) => ({
      id: a.id ?? 0,
      attended_user_id: a.attended_user_id ?? null,
      attended_employee_name: a.attended_employee_name,
      attended_email: a.attended_email ?? null,
      attendance_status: a.attendance_status ?? null,
    })),
    discussion_points: (detail.discussion_points || []).map((p) => ({
      id: p.id ?? 0,
      point_discussed: p.point_discussed,
      responsible_user_id: p.responsible_user_id ?? null,
      responsible_name: p.responsible_name ?? null,
      responsible_email: p.responsible_email ?? null,
      target_date: p.target_date ?? null,
      status: p.status ?? null,
      remarks: p.remarks ?? null,
    })),
  };
}

function MomPage() {
  const navigate = useNavigate();

  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingDetail, setMeetingDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const users = useAuthStore((state) => state.users);
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const companyId = useAuthStore((state) => state.companyId);

  const meetingListRef = useRef(null);
  const todayRef = useRef(null);

  // ================= GET MY MEETINGS =================
  // `status` is optional. When provided, it's sent as a query param.
  const getMyMeetings = async (status) => {
    if (!token || !companyId) {
      console.error("Missing token or companyId for MOM API call");
      return [];
    }

    try {
      const res = await axios.get(`${MOM_API_URL}/mom/${companyId}/my`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: status ? { status } : {},
      });

      return res.data || [];
    } catch (err) {
      console.error("Failed to fetch MOM list:", err);
      return [];
    }
  };

  // ================= GET SINGLE MOM DETAIL =================
  const getMomDetail = async (momId) => {
    if (!token || !companyId || !momId) {
      console.error("Missing token/companyId/momId for MOM detail call");
      return null;
    }

    try {
      const res = await axios.get(`${MOM_API_URL}/mom/${companyId}/${momId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return res.data || null;
    } catch (err) {
      console.error("Failed to fetch MOM detail:", err);
      return null;
    }
  };

  // Pulled out of the useEffect below so it can also be called after a
  // meeting is created/updated elsewhere, to refresh the list.
  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    const status = activeTab === "All" ? undefined : activeTab.toUpperCase();
    const data = await getMyMeetings(status);
    setMeetings(data);
    setLoading(false);
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token, companyId]);

  // Refetch meetings whenever the active tab (status filter) changes
  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Fetch detail whenever a card is clicked
  useEffect(() => {
    if (!selectedMeeting?.id) {
      setMeetingDetail(null);
      return;
    }

    const fetchDetail = async () => {
      setDetailLoading(true);
      const data = await getMomDetail(selectedMeeting.id);
      setMeetingDetail(data);
      setDetailLoading(false);
    };

    fetchDetail();
  }, [selectedMeeting]);

  const getUserName = (created_by) => {
    if (!created_by || !users) return "Unknown"; // ✅ Check if users exists
    const createdByStr = String(created_by);
    const u = users.find((u) => String(u.id) === createdByStr);
    return u ? shortenName(u.name) : "Unknown";
  };

  // ================= PERMISSION CHECK =================
  // Edit / Publish buttons show only if the logged-in user is the one
  // set as "prepared_by" on this meeting.
  const canEditMom = useMemo(() => {
    if (!meetingDetail || !user?.user_id) return false;
    return String(meetingDetail.prepared_by_user_id) === String(user.user_id);
  }, [meetingDetail, user]);

  // Once a MOM is Completed, both Edit and Publish are locked — case
  // insensitive since status casing has drifted between call sites.
  const isCompleted = useMemo(
    () => (meetingDetail?.status || "").toUpperCase() === "COMPLETED",
    [meetingDetail],
  );

  // ============================================================================
  // FIXED: Check if buttons should be shown based on status AND permission
  // Show Cancel and Join buttons only for DRAFT status AND if user prepared the MOM
  // ============================================================================
  const shouldShowActionButtons = useMemo(() => {
    if (!meetingDetail?.status || !canEditMom) return false;
    const status = meetingDetail.status.toUpperCase();
    return status === "DRAFT";
  }, [meetingDetail, canEditMom]);

  // Status filtering is now handled by the API (via activeTab -> status param).
  // Only search filtering happens client-side here.
  const filteredMeetings = useMemo(() => {
    return meetings.filter((m) => {
      const matchesSearch = m.meeting_title
        ?.toLowerCase()
        .includes(search.toLowerCase());

      return matchesSearch;
    });
  }, [meetings, search]);

  const groupedMeetings = useMemo(
    () => groupByMeetingDate(filteredMeetings),
    [filteredMeetings],
  );

  // ============================================================================
  // NEW: Auto-scroll to today's meetings
  // ============================================================================
  useEffect(() => {
    if (todayRef.current && meetingListRef.current) {
      setTimeout(() => {
        todayRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [groupedMeetings]);

  // ================= HANDLE JOIN - Navigate to edit page =================
  const handleJoinMom = () => {
    if (meetingDetail?.id) {
      navigate(`/mom/edit/${meetingDetail.id}`);
    }
  };

  // ================= HANDLE CANCEL MOM =================
  // Changes status to "Cancelled" and sends update to DB
  const handleCancelMom = async () => {
    if (!meetingDetail?.id || !token || !companyId) return;

    setCancelling(true);
    try {
      const payload = buildUpdatePayloadFromDetail(meetingDetail, "Cancelled");
      const res = await axios.put(
        `${MOM_API_URL}/mom/${companyId}/${meetingDetail.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const updated = res.data?.id
        ? res.data
        : { ...meetingDetail, status: "Cancelled" };
      setMeetingDetail(updated);

      toast.success("MOM cancelled successfully");
      fetchMeetings();
    } catch (err) {
      console.error("Failed to cancel MOM:", err);
      toast.error("Failed to cancel MOM. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    handleCancelMom();
  };

  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);

    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="mom-page">
      <div className={`left-panel ${selectedMeeting ? "hide-on-mobile" : ""}`}>
        <div className="left-header">
          <h2>Minutes of Meeting</h2>
          <button className="btn-new" onClick={() => navigate("/mom/add")}>
            + New
          </button>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="status-tabs">
          {statusTabs.map((tab) => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="meeting-list" ref={meetingListRef}>
          {loading ? (
            <p className="empty-state">Loading...</p>
          ) : groupedMeetings.length === 0 ? (
            <p className="empty-state">No meetings found</p>
          ) : (
            groupedMeetings.map((group) => (
              <div key={group.date}>
                <div
                  className="date-label"
                  ref={isTodayDate(group.date) ? todayRef : null}
                >
                  <span>{formatDateLabel(group.date)}</span>
                </div>

                {group.items.map((meeting) => (
                  <div
                    key={meeting.id}
                    className={`meeting-card ${
                      selectedMeeting?.id === meeting.id ? "active" : ""
                    }`}
                    onClick={() => setSelectedMeeting(meeting)}
                  >
                    {/* CONTENT */}
                    <div className="meeting-card-content">
                      {/* ROW 1 */}
                      <div className="card-top">
                        <h4>{meeting.meeting_title}</h4>

                        <span
                          className={`status-badge ${getStatusBadgeClass(
                            meeting.status,
                          )}`}
                        >
                          {meeting.status}
                        </span>
                      </div>

                      {/* ROW 2 / META */}
                      <div className="card-meta">
                        <div className="meeting-meta">
                          <div className="meta-left">
                            {/* DATE + TIME */}
                            <div className="meta-item">
                              <Clock3 className="meta-icon" />

                             <span>
    {formatTime(
      meeting.actual_m_start_time || meeting.start_time
    )}{" "}
    -{" "}
    {formatTime(
      meeting.actual_m_end_time || meeting.end_time
    )}
  </span>
                            </div>

                            {/* MEETING MODE */}
                            <div className="meta-item">
                              <MapPin className="meta-icon" />

                              <span>{meeting.meeting_mode}</span>
                            </div>
                          </div>

                          {/* CREATED BY */}
                          <span className="meta-right">
                            by {getUserName(meeting.created_by)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      <div className={`right-panel ${selectedMeeting ? "show-on-mobile" : ""}`}>
        {!selectedMeeting ? (
          <p className="empty-state">Select a meeting to view details</p>
        ) : detailLoading ? (
          <p className="empty-state">Loading...</p>
        ) : !meetingDetail ? (
          <p className="empty-state">Failed to load meeting</p>
        ) : (
          <div className="detail-view">
            <div className="detail-header">
              <button
                className="btn-back-mobile"
                onClick={() => setSelectedMeeting(null)}
                title="Back"
              >
                <ArrowLeft size={24} />
              </button>
              <h2>{meetingDetail.meeting_title}</h2>

              {/* CHANGED: Edit icon only shows to prepared_by user when NOT completed */}
              {meetingDetail.status !== "Cancelled" &&
                !isCompleted &&
                canEditMom && (
                  <button
                    className="btn-icon"
                    onClick={() => navigate(`/mom/edit/${meetingDetail.id}`)}
                    title="Edit Meeting"
                  >
                    <Pencil size={20} />
                  </button>
                )}

              {/* CHANGED: PDF icon shows to ALL users when completed */}
              {meetingDetail.status !== "Cancelled" && isCompleted && (
                <button
                  className="btn-icon"
                  onClick={() => generateMomPdf(meetingDetail)}
                  title="Download PDF"
                >
                  <img src={pdfIcon} alt="PDF" className="pdf-icon" />
                </button>
              )}
            </div>

            <div className="detail-scroll-area">
              <div className="detail-info-row">
                <div className="info-block">
                  <span className="info-label">DATE</span>
                  <span className="info-value">
                    {formatFullDate(meetingDetail.meeting_date)}
                  </span>
                </div>
                <div className="info-block">
                  <span className="info-label">TIME</span>
                  <span className="info-value">
                    {formatTime(
                      meetingDetail.actual_m_start_time ||
                        meetingDetail.start_time,
                    )}{" "}
                    -{" "}
                    {formatTime(
                      meetingDetail.actual_m_end_time || meetingDetail.end_time,
                    )}
                  </span>
                </div>
                <div className="info-block">
                  <span className="info-label">ORGANISED BY</span>
                  <span
                    className="info-value"
                    title={meetingDetail.organized_by_name}
                  >
                    {shortenName(meetingDetail.organized_by_name)}
                  </span>
                </div>
                <div className="info-block">
                  <span className="info-label">Note Taker</span>
                  <span
                    className="info-value"
                    title={meetingDetail.prepared_by_name}
                  >
                    {shortenName(meetingDetail.prepared_by_name)}
                  </span>
                </div>
                <div className="info-block">
                  <span className="info-label">VENUE</span>
                  <span className="info-value">{meetingDetail.location}</span>
                </div>
              </div>

              <div className="detail-section">
                <h4 className="section-title">
                  <span className="dot" /> Attendees
                </h4>
                <div className="chip-list">
                  {meetingDetail.attendance?.length > 0 ? (
                    meetingDetail.attendance.map((att, idx) => (
                      <span
                        key={idx}
                        className="chip"
                        title={att.attended_employee_name}
                      >
                        {shortenName(att.attended_employee_name)}
                      </span>
                    ))
                  ) : (
                    <span className="empty-inline">No attendees added</span>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h4 className="section-title">
                  <span className="dot" /> Purpose
                </h4>
                <p className="section-text">{meetingDetail.purpose}</p>
              </div>

              <div className="detail-section">
                <h4 className="section-title">
                  <span className="dot" /> Discussion summary
                </h4>

                {meetingDetail.discussion_points?.length > 0 ? (
                  <table className="points-table-header">
                    <thead>
                      <tr>
                        <th>Sr.</th>
                        <th>Points Discussed</th>
                        <th>PIC</th>
                        <th>Target Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {meetingDetail.discussion_points.map((point, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{point.point_discussed}</td>
                          <td title={point.responsible_name}>
                            {shortenName(point.responsible_name)}
                          </td>
                          <td>{formatDate(point.target_date)}</td>
                          <td>
                            <span
                              className={`point-status ${getPointStatusClass(
                                point.status,
                              )}`}
                            >
                              {point.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <span className="empty-inline">
                    No discussion points added
                  </span>
                )}
              </div>
            </div>

            {shouldShowActionButtons && (
              <div className="detail-footer">
                <button
                  className="btn-cancel"
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={cancelling}
                >
                  Cancel
                </button>

                <button
                  className="btn-join"
                  onClick={handleJoinMom}
                  disabled={cancelling}
                >
                  Join
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        open={showCancelConfirm}
        title="Cancel this MOM?"
        message="Are you sure you want to cancel this meeting? This action will mark the MOM as cancelled."
        confirmLabel="Yes, Cancel"
        cancelLabel="No"
        busy={cancelling}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </div>
  );
}

export default MomPage;
